import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SERVICE_ADAPTERS, BandwidthService, Duration } from "./bandwidthServiceAdapters";
import { 
    buildControls,
    getTimeRangeOptions,
    getIntervalOptions,
    toSeconds,
    IntervalLiteral,
    IntervalOption
} from "./timeControls";
import { CartesianGrid, Label, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import GenericCheckbox from "@ui-components/Checkbox/GenericCheckbox";
import { ChartContainer } from "kontrolEdge/components/CloudConnectivity/CloudDetails.tsx/CloudUtilizationChart/ChartContainer";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericCard from "@ui-components/Card/GenericCard";
import { ReactComponent as IconInfo } from "../../../../shared/assets/Icons/circle-info-solid.svg";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppDispatch, useAppSelector } from "@redux/hooks";

interface BandwidthItem { time: string; value: number; }
interface BandwidthUtilization {
  inbound: BandwidthItem[]; outbound: BandwidthItem[];
  timeRange: string; interval: string; unit: string;
}
interface PortPerformanceData {
  portId?: number; connectionId?: number;
  bandwidthUtilization: BandwidthUtilization;
}
function processData(perf: PortPerformanceData | null) {
  if (!perf?.bandwidthUtilization) return [];
  const { inbound, outbound } = perf.bandwidthUtilization;
  return inbound.map((item, i) => {
    const d = new Date(item.time);
    const formattedTime = d.toLocaleString("en-US", {
      timeZone: "America/Mexico_City",
      month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false
    });
    return { time: item.time, inbound: item.value, outbound: outbound[i]?.value ?? 0, formattedTime };
  });
}

type PortType = "physical" | "virtual";

type Props = {
  service: BandwidthService;       // "port" | "cloud"
  id: string;                      // portId or connectionId
  tenantId: number;
  portType?: PortType;
};

export default function BandwidthChartUniversal({ service, id, tenantId, portType }: Props) {
  const dispatch = useAppDispatch();
  const api = useAxiosAuth0();
  const adapter = SERVICE_ADAPTERS[service];
  const slice = useAppSelector(adapter.select);

  const timeRange = slice.timeRange as Duration | null;
  const interval  = slice.interval  as Duration | null;
  const performanceData = slice.performanceData as PortPerformanceData | null;
  const performanceLoading = slice.performanceLoading;
  const performanceError   = slice.performanceError;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const initialized = slice.initialized ?? false;

  // shared controls based on portType rules (virtual: 30m..7d; intervals >=5m, etc.)
  const tc = useMemo(() => buildControls(portType), [portType]);
  type TRKey = (typeof tc.order)[number];

  const CHANGE_INTERVALS_MAP = tc.map;
  const TR_OPTIONS = useMemo(() => getTimeRangeOptions(tc.presets), [tc.presets]);

  // ===== validity & readiness =====
  const configReady = useMemo(() => {
    if (!timeRange || !interval) return false;
    const tr = timeRange as TRKey;
    if (!(tr in CHANGE_INTERVALS_MAP)) return false;
    const options = getIntervalOptions(portType, tr, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval);
    const allowed = options.map(o => o.value);

    const intervalOk = allowed.includes(interval as IntervalLiteral);
    const backendOk  = toSeconds(interval) <= toSeconds(tr);
    return intervalOk && backendOk;
  }, [timeRange, interval, portType, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval]);

  // ===== defaults ONCE per id =====
  const defaultsAppliedRef = useRef<string | null>(null);
  useEffect(() => { defaultsAppliedRef.current = null; }, [id]);

  useEffect(() => {
    if (defaultsAppliedRef.current === id) return;

    const desiredTR = tc.minTimeRange as TRKey; // "30m" for virtual, else first preset
    const opts = getIntervalOptions(portType, desiredTR, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval);
    const smallest = opts[0]?.value as IntervalLiteral | undefined;
    if (!timeRange || timeRange !== desiredTR) {
      // If the slice has setTimeControls, use it to avoid intermediate invalid pairs
      if (adapter.setTimeControls && smallest) {
        dispatch(adapter.setTimeControls({ timeRange: desiredTR, interval: smallest }));
      } else {
        dispatch(adapter.setTimeRange(desiredTR));
        if (smallest) dispatch(adapter.setInterval(smallest));
      }
    } else if (!interval && smallest) {
      dispatch(adapter.setInterval(smallest));
    }

    defaultsAppliedRef.current = id;
  }, [id, timeRange, interval, portType, dispatch, adapter, tc.minTimeRange, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval]);

  // ===== coerce if TR not in this preset set (portType switch) =====
  useEffect(() => {
    const tr = timeRange as TRKey | null;
    if (!tr || tr in CHANGE_INTERVALS_MAP) return;
    const fallback = tc.minTimeRange as TRKey;
    const opts = getIntervalOptions(portType, fallback, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval);
    const smallest = opts[0]?.value as IntervalLiteral | undefined;
    if (adapter.setTimeControls && smallest) {
      dispatch(adapter.setTimeControls({ timeRange: fallback, interval: smallest }));
    } else {
      dispatch(adapter.setTimeRange(fallback));
      if (smallest) dispatch(adapter.setInterval(smallest));
    }
  }, [timeRange, CHANGE_INTERVALS_MAP, tc.minTimeRange, portType, dispatch, adapter, tc.minInterval, tc.maxInterval]);

  // ===== snap interval on TR change =====
  const prevTRRef = useRef<TRKey | null>(null);
  useEffect(() => {
    const tr = timeRange as TRKey | null;
    if (!tr || !(tr in CHANGE_INTERVALS_MAP)) return;
    if (prevTRRef.current !== tr) {
      prevTRRef.current = tr;
      const opts = getIntervalOptions(portType, tr, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval);
      const smallest = opts[0]?.value as IntervalLiteral | undefined;
      // only snap on TR change
      if (smallest && interval !== smallest) dispatch(adapter.setInterval(smallest));
    }
  }, [timeRange, interval, portType, CHANGE_INTERVALS_MAP, dispatch, adapter, tc.minInterval, tc.maxInterval]);

  // ===== FETCH ONLY WHEN READY =====
  useEffect(() => {
    // If we kept an `initialized` flag in slice, also gate on it:
    if (!configReady) return;
    if (!timeRange || !interval) return;

    dispatch(adapter.fetchThunk({
      api, tenantId,
      timeRange, interval,
      id,
    }));
  }, [configReady, timeRange, interval, api, tenantId, id, dispatch, adapter]);

  // ===== dropdown options =====
  const intervalOptions = useMemo<IntervalOption[]>(() => {
    const tr = timeRange as TRKey;
    if (!tr || !(tr in CHANGE_INTERVALS_MAP)) return [];
    return getIntervalOptions(portType, tr, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval);
    }, [timeRange, portType, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval]);

// Time range handler (universal)
const handleTimeRangeSelect = useCallback((choice: string | number) => {
    const opt = typeof choice === "number"
      ? TR_OPTIONS[choice]
      : TR_OPTIONS.find(o => o.label === choice || o.value === choice);
    if (!opt) return;
    const tr = opt.value as TRKey;
    // compute first allowed interval for that TR
    const opts = getIntervalOptions(portType, tr, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval);
    const smallest = opts[0]?.value as IntervalLiteral | undefined;
    // atomic update if available
    if (adapter.setTimeControls && smallest) {
      dispatch(adapter.setTimeControls({ timeRange: tr, interval: smallest }));
    } else {
      dispatch(adapter.setTimeRange(tr));
      if (smallest) dispatch(adapter.setInterval(smallest));
    }
  }, [TR_OPTIONS, portType, CHANGE_INTERVALS_MAP, tc.minInterval, tc.maxInterval, adapter, dispatch]);
  
  
  // Interval handler (universal)
  const handleIntervalSelect = useCallback((choice: string | number) => {
    console.log("[Interval onSelect] choice =", choice, "options =", intervalOptions);
    const opt = typeof choice === "number"
      ? intervalOptions[choice]
      : intervalOptions.find(o => o.label === choice || o.value === choice);
    if (!opt) return;
    dispatch(adapter.setInterval(opt.value as IntervalLiteral));
  }, [intervalOptions, adapter, dispatch]);

  // ===== chart UI =====
  const [showInbound, setShowInbound] = useState(true);
  const [showOutbound, setShowOutbound] = useState(true);
  const chartData = useMemo(() => processData(performanceData), [performanceData]);
  const hasError = !!performanceError || performanceData === null;

  return (
    <div className="py-5 space-y-10">
      <GenericCard
        headerContent={<h3 className="text-body-medium font-bold">Bandwidth Utilization</h3>}
        rightContent={
          <div className="flex content-center">
            {/* Time range */}
            <div className="flex-col mx-5">
              <div className="h-7 flex items-center justify-between">
                <p className="text-sm">Change Time Range</p>
                <div className="w-4" />
              </div>
              <GenericDropdown
                items={TR_OPTIONS.map(o => o.label)}
                value={TR_OPTIONS.find(o => o.value === timeRange)?.label || TR_OPTIONS[0]?.label || "Select Time Range"}
                onSelect={handleTimeRangeSelect}
                defaultLabel="Select Time Range"
                widthClass="w-50"
                maxWidthClass="max-w-[400px]"
                size="sm"
              />
            </div>
            {/* Interval */}
            <div className="flex flex-col mx-5 space-y-1">
              <div className="h-6 flex items-center justify-between">
                <p className=" display text-sm content-center">Change Interval</p>
                <GenericTooltip text="Shorter intervals show more detail; longer ones smooth trends." />
              </div>
              <GenericDropdown
                items={intervalOptions.map(o => o.label)}
                value={intervalOptions.find(o => o.value === interval)?.label ?? "Select Interval"}
                onSelect={handleIntervalSelect}
                defaultLabel="Select Interval"
                widthClass="w-50"
                maxWidthClass="max-w-[400px]"
                size="sm"
              />
            </div>
            {/* Toggles */}
            <div className="flex gap-4 items-center">
              <GenericCheckbox label="Show Inbound" checked={showInbound} onChange={setShowInbound} size="sm" />
              <GenericCheckbox label="Show Outbound" checked={showOutbound} onChange={setShowOutbound} size="sm" />
            </div>
          </div>
        }
        isCollapsible={false}
        bodyContent={
          <>
            <div className="relative h-[300px]">
              {!performanceLoading && hasError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                  <IconInfo className="w-8 h-8 text-yellow-500" />
                  <p className="mt-2 text-sm text-semantic-text-secondary">Performance data is unavailable at the moment.</p>
                </div>
              )}
              <div className="relative w-90% mx-5">
                <ChartContainer
                  config={{
                    inbound: { label: "Inbound", color: "var(--color-primitive-brand-surf-300)" },
                    outbound: { label: "Outbound", color: "var(--color-primitive-brand-surf-700)" },
                  }}
                  className="h-[300px]"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 30, right: 50, left: 40, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="formattedTime" tickLine={false} axisLine={false} tickMargin={10} tick={{ fontSize: 12, fill: "#666" }} interval="preserveStartEnd">
                        <Label value="Time (Mexico City)" position="bottom" offset={20} style={{ textAnchor: "middle", fontSize: 12 }} />
                      </XAxis>
                      <YAxis tickLine={false} axisLine={false} tickMargin={10} tick={{ fontSize: 12, fill: "#666" }} tickFormatter={(v) => `${v}`}>
                        <Label value="Mean Bandwidth Usage" angle={-90} position="insideLeft" offset={-23} style={{ textAnchor: "middle", fontSize: 12 }} />
                        <Label value="(Mbps)" angle={-90} position="insideLeft" offset={-5} style={{ textAnchor: "middle", fontSize: 12 }} />
                      </YAxis>
                      <Tooltip />
                      {showInbound && (
                        <Line type="monotone" dataKey="inbound" name="Inbound" stroke="var(--color-inbound)" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 6 }} />
                      )}
                      {showOutbound && (
                        <Line type="monotone" dataKey="outbound" name="Outbound" stroke="var(--color-outbound)" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 6 }} />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
                {performanceLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
                    <span className="text-lg font-semibold text-gray-700">Loading...</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end my-5 mr-10">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[var(--color-primitive-brand-surf-300)]" />
                  <span className="text-sm">Inbound</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[var(--color-primitive-brand-surf-700)]" />
                  <span className="text-sm">Outbound</span>
                </div>
              </div>
            </div>
          </>
        }
      />
    </div>
  );
}
